/**
 * A tested plan, in three layers (UX review, 17 Oct 2026; owner: "make this screen look world class").
 *
 *   1. The decision — a name in everyday words, one big number ("left to the family after about 5
 *      years"), the gain against doing nothing, and two bars: how much more than doing nothing, and
 *      how much is tied up in the deposit. The trade-off between two plans is visible without scrolling.
 *   2. The three numbers a family asks about — care costs, the Age Pension, each year's shortfall —
 *      each against doing nothing, each with a "why?" that opens its breakdown.
 *   3. When the money runs out, the plan in three short lines, and the three buttons the owner ruled
 *      on (2 Oct 2026): Explain this / What could go wrong / Choose this plan.
 *
 * Everything else the card used to show — the allocation grid, the money flow, the six-tile snapshot
 * with its pop-overs, the funding note — is still here, behind "Show the detail". Nothing is removed
 * (an adviser reads it; the Statement of Advice is built from it); it is no longer the first thing a
 * son or daughter has to read. The figures are the engine row's own, quoted; this file computes nothing.
 */
import React from 'react';
import { BookOpen, CheckCircle, ChevronDown, ShieldAlert } from 'lucide-react';
import { formatCurrency } from '../../utils/index.ts';

export interface PlanCardNumber {
    label: string;
    value: number;
    unit?: string;
    /** The line under the figure — "$40,848 of it is the daily room fee · doing nothing $99,809". */
    vs: React.ReactNode;
    tone?: 'neutral' | 'good' | 'bad';
    /** Opens the breakdown for this figure (the tile pop-over in the detail layer). */
    onWhy?: () => void;
}

export interface EnginePlanCardProps {
    /** "Leaves the most", "Nearly the most, with less tied up". */
    chip: string | null;
    chipTone?: 'blue' | 'neutral';
    title: string;
    sub?: string;
    /** Left to the family. */
    legacy: number;
    /** Against doing nothing; null when the baseline is not available. */
    legacyDelta: number | null;
    horizonYears: number;
    /** The two bars: the gain against doing nothing, and the deposit, each as a share of the biggest on the page. */
    bars: { gainPct: number; tiedUp: number; tiedUpPct: number } | null;
    numbers: PlanCardNumber[];
    /** "When the money runs out: …" — one line, from the funding runway. */
    runline?: React.ReactNode;
    /** Amber "Worth knowing" — a horizon warning, a nil-residual annuity. */
    worthKnowing?: React.ReactNode;
    /** The plan in two or three short lines. */
    lines: React.ReactNode[];
    chosen: boolean;
    onExplain: () => void;
    onRisks: () => void;
    onChoose: () => void;
    /** "Choose this plan", or "Choose this instead" when another plan is already chosen. */
    chooseLabel: string;
    /** Everything the card used to show, behind "Show the detail". */
    detail?: React.ReactNode;
    detailOpen: boolean;
    onToggleDetail: () => void;
    /** The strip under a chosen plan: saved, the document, build on it yourself. */
    chosenStrip?: React.ReactNode;
    /** Anything the panel shows above the card body (a set-aside notice, the lower-deposit note). */
    notices?: React.ReactNode;
    /**
     * Why a second card is shown (the trade-off against the leader), beside the estate figure (owner, 23 Oct 2026):
     * a banner above the card pushed its title and figures out of line with the card beside it, and beside the three
     * figures it squeezed them into half the width. Stacks under the estate figure on a narrow card.
     */
    whyShown?: React.ReactNode;
    testId?: string;
    /** The second bar's label — "Tied up in the deposit"; Support at home: "Moved into the home". */
    tiedUpLabel?: string;
    /** The words after "Show the detail —". */
    detailHint?: string;
    /** Extra action under the three buttons (Support at home's "If care needs increase"). */
    extraAction?: React.ReactNode;
    /** Leave out the second bar when nothing is tied up (a plan that moves no money). */
    hideEmptyTiedUp?: boolean;
    /** Bullets rather than numbers for the short lines (Support at home's mock-up; aged care numbers them). */
    linesBulleted?: boolean;
    /** What the estate is measured against: "doing nothing" (aged care, Support at home), "staying put" (the village). */
    baselineWord?: string;
    /** In place of the "+$X vs …" line, e.g. "The most of the ways to move" (the village, decided to move). */
    deltaNote?: React.ReactNode;
    /**
     * Cards side by side line up row by row (owner, 20 Oct 2026: "same sized boxes, symmetry of buttons"):
     * the card becomes four rows of its parent's grid (`grid-rows-subgrid`) — the plan, the numbers, the
     * words with the buttons, and what sits under the buttons (the chosen strip, the detail), so the buttons
     * share a level even when only one card is chosen. The parent must be a grid.
     */
    aligned?: boolean;
    /** Above the three buttons: what must be confirmed before choosing (a hand-built income-only annuity's tick). */
    beforeChoose?: React.ReactNode;
    /** The choose button is held until `beforeChoose` is answered. */
    chooseHeld?: boolean;
    /**
     * Under the plan's lines: what the family receives from its guaranteed income if death comes early
     * (AnnuityFamilyPanel; guaranteed income review, 21 Oct 2026). Part of the card wherever it appears.
     */
    familyPanel?: React.ReactNode;
    /** The words under the estate figure, in place of "left to the family after about N years" (granny flat: "in your estate after about 10 years"). */
    legacyCaption?: React.ReactNode;
    /** The first bar's label when the plan leaves less, in place of "Less than {baselineWord}" (granny flat: "Less in the estate"). */
    lessLabel?: string;
}

const Bar = ({ label, pct, value, tone }: { label: string; pct: number; value: string; tone: 'good' | 'amber' | 'bad' }) => (
    <div className="grid grid-cols-[104px_minmax(0,1fr)_auto] items-center gap-2 text-[13px] sm:grid-cols-[150px_minmax(0,1fr)_auto] sm:gap-3">
        <span className="text-slate-600">{label}</span>
        <span className="h-2 overflow-hidden rounded-full bg-slate-100" aria-hidden>
            <span className={`block h-full rounded-full ${tone === 'good' ? 'bg-emerald-500' : tone === 'bad' ? 'bg-rose-500' : 'bg-amber-500'}`} style={{ width: `${Math.max(3, Math.min(100, pct))}%` }} />
        </span>
        <span className={`font-bold tabular-nums ${tone === 'good' ? 'text-emerald-700' : 'text-slate-900'}`}>{value}</span>
    </div>
);

const EnginePlanCard = ({
    chip, chipTone = 'neutral', title, sub, legacy, legacyDelta, horizonYears, bars, numbers, runline, worthKnowing, lines,
    chosen, onExplain, onRisks, onChoose, chooseLabel, detail, detailOpen, onToggleDetail, chosenStrip, notices, whyShown, testId,
    tiedUpLabel = 'Tied up in the deposit', detailHint = 'where the deposit comes from, the yearly figures and every calculation', extraAction, hideEmptyTiedUp = false, linesBulleted = false,
    baselineWord = 'doing nothing', deltaNote, aligned = false, beforeChoose, chooseHeld = false, familyPanel, legacyCaption, lessLabel,
}: EnginePlanCardProps) => {
    const yrs = `${horizonYears} ${horizonYears === 1 ? 'year' : 'years'}`;
    // Under the buttons: the chosen strip, an extra action, the detail.
    const below = (
        <>
            {chosen && chosenStrip}
            {extraAction}
            {detail && (
                <div className="mt-4 border-t border-slate-200 pt-3">
                    <button type="button" onClick={onToggleDetail} aria-expanded={detailOpen} className="text-left text-[13px] font-semibold text-blue-700 hover:text-blue-900">
                        {/* The label never breaks; the hint after it wraps (19 Oct 2026: "Show the / detail"). */}
                        <span className="inline-flex items-center gap-1.5 whitespace-nowrap"><ChevronDown size={15} className={`transition-transform ${detailOpen ? 'rotate-180' : ''}`} /> {detailOpen ? 'Hide the detail' : 'Show the detail'}</span>{' '}
                        <span className="font-normal text-slate-500">— {detailHint}</span>
                    </button>
                    {detailOpen && <div className="mt-4">{detail}</div>}
                </div>
            )}
        </>
    );
    return (
        <div data-testid={testId} className={`${aligned ? 'row-span-4 grid grid-rows-subgrid gap-0' : 'flex flex-col'} overflow-hidden rounded-2xl border bg-white shadow-card ${chosen ? 'border-emerald-500 ring-1 ring-emerald-200' : 'border-slate-200'}`}>
            <div>
            {notices}
            <div className="p-5 pb-0">
                {chip && (
                    <span className={`text-[12.5px] font-semibold ${chipTone === 'blue' ? 'text-brand-navy' : 'text-slate-500'}`}>{chip}</span>
                )}
                <h3 className="mt-2 text-[20px] font-bold leading-snug tracking-tight text-slate-900">{title}</h3>
                {sub && <p className="mt-1 text-[13px] leading-relaxed text-slate-600">{sub}</p>}
                <div className={`mt-4 ${whyShown ? 'grid grid-cols-1 gap-3 sm:grid-cols-[auto_minmax(0,1fr)] sm:items-start sm:gap-5' : ''}`}>
                <div>
                    <p className="text-[36px] font-bold leading-none tabular-nums tracking-tight text-brand-navy">{formatCurrency(Math.round(legacy))}</p>
                    <p className="mt-1 text-[13px] text-slate-600">{legacyCaption ?? <>left to the family after about {yrs}</>}</p>
                    {deltaNote ? (
                        <p className="mt-0.5 text-[13px] font-bold text-brand-navy">{deltaNote}</p>
                    ) : legacyDelta != null && (
                        <p className={`mt-0.5 text-[13px] font-bold tabular-nums ${legacyDelta >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                            {legacyDelta >= 0 ? '+' : '−'}{formatCurrency(Math.abs(Math.round(legacyDelta)))} vs {baselineWord}
                        </p>
                    )}
                </div>
                {whyShown && (
                    <div className="rounded-lg border border-slate-200 border-l-4 border-l-blue-500 bg-white px-3.5 py-2.5 text-[13px] leading-relaxed text-slate-700" data-testid="trade-off-note">
                        <strong className="text-slate-900">Why it is shown:</strong> {whyShown}
                    </div>
                )}
                </div>
                {bars && (
                    <div className="mt-4 space-y-2">
                        {legacyDelta != null && <Bar label={legacyDelta >= 0 ? `More than ${baselineWord}` : (lessLabel ?? `Less than ${baselineWord}`)} pct={bars.gainPct} value={`${legacyDelta >= 0 ? '+' : '−'}${formatCurrency(Math.abs(Math.round(legacyDelta)))}`} tone={legacyDelta >= 0 ? 'good' : 'bad'} />}
                        {(bars.tiedUp > 0 || !hideEmptyTiedUp) && <Bar label={tiedUpLabel} pct={bars.tiedUpPct} value={formatCurrency(Math.round(bars.tiedUp))} tone="amber" />}
                    </div>
                )}
            </div>
            </div>

            <div className="mt-4 divide-y divide-slate-100 border-y border-slate-200 bg-slate-50/70 self-start w-full">
                {numbers.map((n) => (
                    <div key={n.label} className="px-5 py-3">
                        <p className="text-[12px] font-semibold text-slate-500">{n.label}</p>
                        <p className={`mt-0.5 text-[20px] font-bold leading-none tabular-nums ${n.tone === 'good' ? 'text-emerald-700' : n.tone === 'bad' ? 'text-rose-700' : 'text-slate-900'}`}>
                            {formatCurrency(Math.round(n.value))}{n.unit && <span className="ml-1 text-[12px] font-medium text-slate-500">{n.unit}</span>}
                        </p>
                        <p className="mt-1 text-[12px] text-slate-500">
                            {n.vs}
                            {n.onWhy && <> · <button type="button" onClick={n.onWhy} className="font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900">why?</button></>}
                        </p>
                    </div>
                ))}
            </div>

            <div className={`flex flex-1 flex-col ${aligned ? 'px-5 pt-5' : 'p-5'}`}>
                {runline && <div className="text-[13px] leading-relaxed text-slate-700">{runline}</div>}
                {/* The caller composes the words ("Worth knowing." + the note, or a product's one-line disclosure), so a card can carry more than one. */}
                {worthKnowing && (
                    <div className="mt-3 space-y-1.5 rounded-lg border border-slate-200 border-l-4 border-l-amber-400 bg-white py-2 pl-3 pr-2 text-[13px] leading-relaxed text-slate-700">
                        {worthKnowing}
                    </div>
                )}
                {lines.length > 0 && (
                    linesBulleted ? (
                        <ul className="mt-3 list-disc space-y-1.5 pl-5 text-[13px] leading-relaxed text-slate-700">
                            {lines.map((l, i) => <li key={i}>{l}</li>)}
                        </ul>
                    ) : (
                        <ol className="mt-3 list-decimal space-y-1.5 pl-5 text-[13px] leading-relaxed text-slate-700">
                            {lines.map((l, i) => <li key={i}>{l}</li>)}
                        </ol>
                    )
                )}
                {familyPanel && <div className="mt-4">{familyPanel}</div>}

                {/* mt-auto: two cards side by side are the same height, and the buttons sit at the same
                    level on both — any spare room goes ABOVE the buttons, under the last line, not
                    below them (owner, 18 Oct 2026). */}
                {beforeChoose && !chosen && <div className="mt-4">{beforeChoose}</div>}
                <div className="mt-auto flex flex-wrap gap-2 pt-4">
                    <button type="button" onClick={onExplain} className="inline-flex min-w-[8.5rem] flex-1 items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-[13px] font-semibold text-slate-800 hover:border-blue-300 hover:bg-blue-50"><BookOpen size={15} /> Explain this</button>
                    <button type="button" onClick={onRisks} className="inline-flex min-w-[8.5rem] flex-1 items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-[13px] font-semibold text-slate-800 hover:border-blue-300 hover:bg-blue-50"><ShieldAlert size={15} /> What could go wrong</button>
                    <button
                        type="button"
                        onClick={chooseHeld && !chosen ? undefined : onChoose}
                        disabled={chooseHeld && !chosen}
                        aria-pressed={chosen}
                        data-testid="plan-card-choose"
                        className={`inline-flex min-w-[8.5rem] flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 text-[13px] font-semibold ${chosen ? 'border border-emerald-500 bg-white text-emerald-800' : chooseHeld ? 'cursor-not-allowed bg-blue-600 text-white opacity-50' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                    >
                        <CheckCircle size={15} /> {chosen ? 'Chosen ✓' : chooseLabel}
                    </button>
                </div>
                {!aligned && below}
            </div>
            {aligned && <div className="px-5 pb-5">{below}</div>}
        </div>
    );
};

export default EnginePlanCard;
